import axios from 'axios';
import { UbyportMappedOption } from './api.publicCheckin';

export type { UbyportMappedOption };

const API_BASE = process.env.REACT_APP_API_URL || '/api';

/**
 * One end of a stay as the lists draw it: the hour to show, what stage put it there, and the
 * apartment's standard for the tooltip. `state` null means nothing has been asked for, so `time`
 * and `standard` are the same value.
 */
export interface StayHour {
  time: string;
  state: 'REQUESTED' | 'APPROVED' | 'PAID' | null;
  standard: string;
}

export interface Reservation {
  id?: number;
  apartmentId: number;
  apartmentName?: string;
  guestId?: number;
  guestName: string;
  checkInDate: string; // ISO date string
  checkOutDate: string;
  numberOfGuests: number | null;
  purposeCode?: string;
  status?: string;
  notes?: string;
  isConfirmed?: boolean;

  // Ubyport tracking
  ubyportStatus?: 'NOT_SENT' | 'SENDING' | 'SENT' | 'PARTIAL' | 'SEND_FAILED' | 'SEND_UNAVAILABLE' | string;
  ubyportSentAt?: string;
  ubyportError?: string;
  ubyportReference?: string;
  ubyportSubmittedBy?: 'MANUAL' | 'AUTO_TRIGGER' | 'AUTO_SWEEP' | 'AUTO_RETRY' | string;

  // Public check-in lock
  isPublicCheckinLocked?: boolean;
  publicCheckinLockedAt?: string;
  publicCheckinFormToken?: string;
  publicCheckinFormUrl?: string;
  guestbookStatus?: 'COMPLETED' | 'INCOMPLETE' | 'NOT_FILLED' | 'DEPRECATED' | string;
  importedPlatform?: string;
  /** Channel the guest actually booked on, when the reservation came via a channel manager. */
  pmsChannelType?: string;
  importedCalendarId?: number;
  externalCalendarUid?: string;
  managerAttentionRequired?: boolean;
  syncCollision?: boolean;
  syncCollisionReason?: string;
  collisionResolution?: 'NONE' | 'INACTIVE' | 'APPROVED' | string;
  collisionResolutionManual?: boolean;

  // TTLock smart lock PIN
  apartmentTtlockEnabled?: boolean | null;
  ttlockPinCode?: string | null;
  ttlockPinError?: string | null;
  /** True when ttlockPinCode came from the cloud-only offline-passcode fallback (no push to device needed) */
  ttlockPinIsOffline?: boolean | null;

  /** ISO datetime when all guest slots were completed. Null = not yet complete. */
  formCompletedAt?: string | null;
  /** ISO datetime when any guest first opened the check-in guide. Null = never opened. */
  guideFirstOpenedAt?: string | null;
  /** Hours this stay runs on. See StayHours — exactly one hour per end, coloured by state. */
  checkinHours?: StayHour | null;
  checkoutHours?: StayHour | null;
  /** One luggage indicator for the row: absent when nothing was requested. See LuggageStatusIcon. */
  luggageStatus?: 'PENDING' | 'PAID' | null;

  source?: string | null;
  guestEmail?: string | null;
  guestPhone?: string | null;
  paymentStatus?: 'N_A' | 'PENDING_PAYMENT' | 'PAID' | null;
  paymentConfirmedAt?: string | null;

  // City tax — independent from the direct-booking deposit above. Only meaningful when
  // cityTaxCollectionEnabled is true for the apartment.
  cityTaxCollectionEnabled?: boolean;
  cityTaxStatus?: 'PAID' | 'UNPAID' | 'EXEMPT' | string;
  cityTaxAmount?: number | null;
  cityTaxCurrency?: string | null;
  cityTaxPaidAt?: string | null;
  /** Cumulative amount actually recorded as paid (Stripe + manual/preset). */
  cityTaxAmountPaid?: number | null;
  /** Live-recalculated total owed from current guest data — may differ from cityTaxAmount (the
   * snapshot at last payment/override) after a guest correction. */
  cityTaxAmountOwed?: number | null;
  /** owed - paid when positive, else null. */
  cityTaxMissingAmount?: number | null;
  /** paid - owed when positive, else null. Informational only — never affects guide access. */
  cityTaxOverpaidAmount?: number | null;

  createdAt?: string;
  updatedAt?: string;
}

export interface ReservationFilter {
  apartmentId?: number;
  checkInAfter?: string;
  checkOutAfter?: string;
  checkOutBefore?: string;
  guestName?: string;
  guestPhone?: string;
  sortBy?: 'apartment' | 'checkInDate' | 'checkOutDate';
  sortDirection?: 'asc' | 'desc';
  page?: number;
  size?: number;
}

export interface ReservationPageResponse {
  content: Reservation[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
}

export const getReservationById = async (id: number, token: string): Promise<Reservation> => {
  const res = await axios.get(`${API_BASE}/reservations/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getMyReservations = async (token: string): Promise<Reservation[]> => {
  const res = await axios.get(`${API_BASE}/reservations`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getAttentionRequiredReservations = async (token: string): Promise<Reservation[]> => {
  const res = await axios.get(`${API_BASE}/reservations/attention-required`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getReservationsByDate = async (date: string, token: string): Promise<Reservation[]> => {
  const res = await axios.get(`${API_BASE}/reservations/date/${date}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const getReservationsByDateRange = async (startDate: string, endDate: string, token: string): Promise<Reservation[]> => {
  const res = await axios.get(`${API_BASE}/reservations/range`, {
    params: { startDate, endDate },
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const createReservation = async (reservation: Reservation, token: string): Promise<Reservation> => {
  const res = await axios.post(`${API_BASE}/reservations`, reservation, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const updateReservation = async (id: number, reservation: Reservation, token: string): Promise<Reservation> => {
  const res = await axios.put(`${API_BASE}/reservations/${id}`, reservation, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const deleteReservation = async (id: number, token: string): Promise<void> => {
  await axios.delete(`${API_BASE}/reservations/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
};

export const confirmReservation = async (id: number, token: string): Promise<Reservation> => {
  const res = await axios.post(`${API_BASE}/reservations/${id}/confirm`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const searchReservations = async (filter: ReservationFilter, token: string): Promise<ReservationPageResponse> => {
  const res = await axios.post(`${API_BASE}/reservations/search`, filter, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const unlockPublicCheckin = async (id: number, reason: string | undefined, token: string): Promise<Reservation> => {
  const res = await axios.post(`${API_BASE}/reservations/${id}/unlock-public-checkin`, {
    reason,
  }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const generatePublicCheckinForm = async (id: number, token: string): Promise<Reservation> => {
  const res = await axios.post(`${API_BASE}/reservations/${id}/generate-public-checkin-form`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// Manager guest form slot management

export interface ManagerMemberSlot {
  slotIndex: number;
  isComplete: boolean;
  firstName?: string;
  surname?: string;
  dateOfBirth?: string;
  phone?: string;
  address?: string;
  country?: string;
  purposeOfStay?: string;
  documentType?: string;
  documentNumber?: string;
  documentCountry?: string;
  visaNumber?: string;
  residencePermitNumber?: string;
  hasDisabilityCard?: boolean;
  signatureSvg?: string;
  createdAt?: string;
  updatedAt?: string;
  ubyportSentAt?: string;
  ubyportError?: string;
}

export interface ManagerCheckinForm {
  reservationId: number;
  apartmentName: string;
  checkInDate: string;
  checkOutDate: string;
  numberOfGuests: number | null;
  isCompleted: boolean;
  members: ManagerMemberSlot[];
  states: UbyportMappedOption[];
  purposes: UbyportMappedOption[];
  ubyportStatus?: 'NOT_SENT' | 'SENDING' | 'SENT' | 'PARTIAL' | 'SEND_FAILED' | 'SEND_UNAVAILABLE' | string;
  ubyportError?: string;
  ubyportSubmittedBy?: 'MANUAL' | 'AUTO_TRIGGER' | 'AUTO_SWEEP' | 'AUTO_RETRY' | string;
  guestEmail?: string;
  formCreatedAt?: string;
  apartmentPublicKey?: string;
}

export interface UpdateMemberSlotRequest {
  firstName?: string;
  surname?: string;
  dateOfBirth?: string;
  phone?: string;
  address?: string;
  country?: string;
  purposeOfStay?: string;
  documentType?: string;
  documentNumber?: string;
  documentCountry?: string;
  visaNumber?: string;
  residencePermitNumber?: string;
  hasDisabilityCard?: boolean;
}

export const getManagerCheckinForm = async (reservationId: number, token: string): Promise<ManagerCheckinForm> => {
  const res = await axios.get(`${API_BASE}/reservations/${reservationId}/checkin-form`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const updateManagerMemberSlot = async (
  reservationId: number,
  slotIndex: number,
  payload: UpdateMemberSlotRequest,
  token: string
): Promise<ManagerMemberSlot> => {
  const res = await axios.put(
    `${API_BASE}/reservations/${reservationId}/checkin-form/slot/${slotIndex}`,
    payload,
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
  );
  return res.data;
};

export const resetManagerMemberSlot = async (
  reservationId: number,
  slotIndex: number,
  token: string
): Promise<void> => {
  await axios.delete(
    `${API_BASE}/reservations/${reservationId}/checkin-form/slot/${slotIndex}`,
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
  );
};

export const resetAllGuests = async (reservationId: number, token: string): Promise<Reservation> => {
  const res = await axios.post(
    `${API_BASE}/reservations/${reservationId}/checkin-form/reset-all`,
    {},
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
  );
  return res.data;
};

export const submitAllToUbyport = async (
  reservationId: number,
  token: string
): Promise<Reservation> => {
  const res = await axios.post(
    `${API_BASE}/reservations/${reservationId}/submit-to-ubyport`,
    {},
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
  );
  return res.data;
};

export const resendCheckinEmail = async (reservationId: number, token: string): Promise<void> => {
  await axios.post(
    `${API_BASE}/reservations/${reservationId}/checkin-form/resend-email`,
    {},
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
  );
};

export interface TtlockCodeWindow {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
}

export const generateTtlockPin = async (reservationId: number, token: string, window?: TtlockCodeWindow): Promise<Reservation> => {
  const res = await axios.post(
    `${API_BASE}/reservations/${reservationId}/generate-ttlock-pin`,
    window || {},
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
  );
  return res.data;
};

export const generateTtlockPinOffline = async (reservationId: number, token: string, window?: TtlockCodeWindow): Promise<Reservation> => {
  const res = await axios.post(
    `${API_BASE}/reservations/${reservationId}/generate-ttlock-pin-offline`,
    window || {},
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
  );
  return res.data;
};

export const approveDirectBooking = async (id: number, token: string): Promise<Reservation> => {
  const res = await axios.post(`${API_BASE}/reservations/${id}/approve`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const rejectDirectBooking = async (id: number, token: string, message?: string): Promise<Reservation> => {
  const res = await axios.post(`${API_BASE}/reservations/${id}/reject`, message ? { message } : {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

export const markReservationPaid = async (id: number, token: string): Promise<Reservation> => {
  const res = await axios.post(`${API_BASE}/reservations/${id}/mark-paid`, {}, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

/** Manager-only escape hatch: manually set city tax to "PAID" or "UNPAID" — cash payments,
 * Stripe hiccups, or resetting a reservation for a fresh test run. */
export const setCityTaxPaymentStatus = async (
  id: number, status: 'PAID' | 'UNPAID' | 'EXEMPT', token: string, amount?: number
): Promise<Reservation> => {
  const res = await axios.put(`${API_BASE}/reservations/${id}/city-tax-status`, { status, amount }, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

// ── Activity trail ──────────────────────────────────────────────────────────

/**
 * One entry in a reservation's history. Read-only: the trail is appended to by the backend at the
 * moment each thing happens and is never edited here.
 */
export interface ReservationActivityEvent {
  at: string;
  type: string;
  /** guest | system | stripe | manager:<id> | pms:<provider> */
  actor: string;
  data: Record<string, unknown>;
}

export const getReservationActivity = async (
  reservationId: number,
  token: string,
): Promise<ReservationActivityEvent[]> => {
  const res = await axios.get<ReservationActivityEvent[]>(
    `${API_BASE}/reservations/${reservationId}/activity`,
    { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
  );
  return res.data;
};
